/**
 * Chat-message HTML sanitizer.
 *
 * Chat messages are produced by a TipTap rich-text composer and stored as
 * HTML, then rendered via dangerouslySetInnerHTML in `MessageItem`. Without
 * sanitization, a user could send `<img src=x onerror=...>` and execute
 * script in every viewer's browser — a one-message account-takeover.
 *
 * The composer applies its own client-side sanitizer at compose time, but
 * that runs in the *sender's* browser and is trivially bypassed by hitting
 * the API directly with crafted HTML. The render-time call here, plus the
 * server-side ingestion sanitizer in `chat-operations.ts`, are the two
 * places we actually trust. Keep the allow-list here in lockstep with the
 * one in `chat-operations.ts` — any divergence is a vulnerability.
 *
 * Allow-list mirrors the TipTap schema in `MessageInput.tsx`: paragraph,
 * inline emphasis, code blocks, ordered/unordered lists, links (with
 * javascript:/data: URIs forbidden by DOMPurify), blockquotes, and
 * mention spans (`<span data-mention data-user-id>`).
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'strike',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'span',
];

const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'class',
  'data-mention',
  'data-user-id',
  'data-display-name',
];

// DOMPurify's ALLOWED_ATTR is a single global list, so it cannot express the
// server's per-tag scoping (href/target/rel only on <a>; mention data-* only on
// <span>). We enforce that policy — and force rel="noopener noreferrer" on
// new-tab links (reverse-tabnabbing defense-in-depth; modern browsers imply it,
// we make it explicit) — in an attribute hook. Registering globally on the
// singleton is safe: this module is the client's only DOMPurify consumer, and
// the hook is idempotent so any duplicate registration (e.g. test reloads) is
// harmless. Keep this scoping in lockstep with the server allow-list in
// packages/db-operations/src/chat-operations.ts.
const ANCHOR_ONLY_ATTRS = ['href', 'target', 'rel'];
const SPAN_ONLY_ATTRS = ['data-mention', 'data-user-id', 'data-display-name'];

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  const el = node as Element;
  if (typeof el.tagName !== 'string') return;
  const tag = el.tagName.toLowerCase();

  if (tag !== 'a') {
    for (const attr of ANCHOR_ONLY_ATTRS) el.removeAttribute(attr);
  }
  if (tag !== 'span') {
    for (const attr of SPAN_ONLY_ATTRS) el.removeAttribute(attr);
  }
  if (tag === 'a' && el.getAttribute('target') === '_blank') {
    el.setAttribute('rel', 'noopener noreferrer');
  }
});

export function sanitizeChatHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Disable the blanket data-* allowance. The mention data-* attrs we keep are
    // enumerated in ALLOWED_ATTR (explicitly-listed attrs survive this), and the
    // hook above scopes them to <span>; any other data-* is dropped.
    ALLOW_DATA_ATTR: false,
    // NOTE: do NOT add `USE_PROFILES`. DOMPurify treats it as authoritative and
    // IGNORES ALLOWED_TAGS / ALLOWED_ATTR when set, silently widening the
    // allow-list (e.g. permitting <img>) and stripping mention data-*. The
    // explicit lists above are the contract. DOMPurify's defaults already block
    // javascript: and data: URIs in href, leaving http/https/mailto/relative
    // URLs intact.
  });
}
